import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Workflow, Zap, Search, Check } from "lucide-react";
import { apiFetch, API } from "@/utils/api";

const CATEGORIES = [
  { value: "proveedores", label: "Proveedores" },
  { value: "suministros", label: "Suministros" },
  { value: "servicios", label: "Servicios profesionales" },
  { value: "publicidad", label: "Publicidad" },
  { value: "seguros", label: "Seguros" },
  { value: "alquiler", label: "Alquileres" },
  { value: "bancarios", label: "Gastos bancarios" },
  { value: "nominas", label: "Pago de nómina" },
  { value: "impuestos", label: "Impuestos" },
  { value: "clientes", label: "Cobros clientes" },
  { value: "otros", label: "Otros" },
];

function RuleForm({ rule, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: rule?.name || "",
    priority: rule?.priority || 0,
    conditions: {
      description: rule?.conditions?.description || "",
    },
    actions: {
      category: String(rule?.actions?.account_code || "").startsWith("640") ? "nominas" : (rule?.actions?.category || ""),
      account_code: (rule?.actions?.category === "nominas" || String(rule?.actions?.account_code || "").startsWith("640")) ? "" : (rule?.actions?.account_code || ""),
    },
    is_active: rule?.is_active ?? true,
  });

  // Plan de cuentas completo del usuario (no una lista fija): permite elegir
  // CUALQUIER cuenta del PGC, buscando por código o nombre, igual que en conciliación.
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState("");

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await apiFetch(`${API}/accounting/chart?order=usage`);
        if (res.ok) {
          const data = await res.json();
          setAccounts((data.accounts || []).filter((a) => !String(a.code).startsWith("640")));
        }
      } catch {
        toast.error("Error al cargar cuentas");
      }
    };
    fetchAccounts();
  }, []);

  const selectedCode = formData.actions.account_code;
  const selectedAccount = accounts.find((a) => a.code === selectedCode);
  const filteredAccounts = accounts.filter((acc) => {
    if (!accountSearch) return true;
    const q = accountSearch.toLowerCase();
    return acc.code.includes(q) || acc.name.toLowerCase().includes(q);
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("El nombre es obligatorio");
      return;
    }
    
    if (!formData.conditions.description) {
      toast.error("Debes especificar un texto a buscar");
      return;
    }

    onSave({
      ...formData,
      actions: { ...formData.actions, account_code: formData.actions.category === "nominas" ? null : formData.actions.account_code },
      conditions: {
        description: {
          operator: "contains",
          value: formData.conditions.description,
        },
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 min-w-0">
      <div>
        <Label>Nombre de la regla</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ej: Pagos a Telefónica"
          data-testid="rule-name-input"
        />
      </div>

      <div>
        <Label>Prioridad (mayor = primero)</Label>
        <Input
          type="number"
          value={formData.priority}
          onChange={(e) =>
            setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })
          }
          data-testid="rule-priority-input"
        />
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">Condición</h4>
        <div>
          <Label>Descripción contiene</Label>
          <Input
            value={formData.conditions.description}
            onChange={(e) =>
              setFormData({
                ...formData,
                conditions: { ...formData.conditions, description: e.target.value },
              })
            }
            placeholder="Ej: TELEFONICA"
            data-testid="rule-condition-input"
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">Acciones</h4>
        <div className="space-y-3">
          <div>
            <Label>Categoría</Label>
            <Select
              value={formData.actions.category}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  actions: { ...formData.actions, category: value, account_code: value === "nominas" ? "" : formData.actions.account_code },
                })
              }
            >
              <SelectTrigger data-testid="rule-category-select">
                <SelectValue placeholder="Seleccionar categoría" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Cuenta contable</Label>
            {formData.actions.category === "nominas" && <p className="text-sm text-slate-600">Pago de nómina · Sin conciliar. No genera gasto ni asiento.</p>}
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar cuenta (código o nombre)..."
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                className="pl-10"
                data-testid="rule-account-search"
              />
            </div>
            <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-lg mt-2">
              {filteredAccounts.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-3">
                  {accounts.length === 0 ? "Cargando cuentas..." : "Sin cuentas"}
                </p>
              ) : (
                filteredAccounts.slice(0, 100).map((acc) => (
                  <button
                    key={acc.account_id || acc.code}
                    disabled={formData.actions.category === "nominas"}
                    type="button"
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-2 transition-colors ${
                      selectedCode === acc.code
                        ? "bg-blue-50 text-blue-900"
                        : "text-slate-700"
                    }`}
                    onClick={() =>
                      setFormData({
                        ...formData,
                        actions: { ...formData.actions, account_code: acc.code },
                      })
                    }
                    data-testid={`rule-account-option-${acc.code}`}
                  >
                    <span className="font-mono text-xs font-medium text-blue-600 w-20 shrink-0">
                      {acc.code}
                    </span>
                    <span className="truncate flex-1 min-w-0">{acc.name}</span>
                    {acc.usage_count > 0 && (
                      <span
                        className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0"
                        title={`Usada ${acc.usage_count} vez/veces`}
                      >
                        ×{acc.usage_count}
                      </span>
                    )}
                    {selectedCode === acc.code && (
                      <Check className="w-4 h-4 text-blue-600 shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
            {selectedCode && (
              <div className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1 mt-2 flex items-center justify-between gap-2">
                <span className="truncate min-w-0">
                  Seleccionada: {selectedCode}
                  {selectedAccount ? ` - ${selectedAccount.name}` : ""}
                </span>
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-600 shrink-0"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      actions: { ...formData.actions, account_code: "" },
                    })
                  }
                >
                  quitar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-2">
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, is_active: checked })
            }
            data-testid="rule-active-switch"
          />
          <Label>Regla activa</Label>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" data-testid="save-rule-btn">
          Guardar Regla
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function BankRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const fetchRules = async () => {
    try {
      const response = await apiFetch(`${API}/bank/rules`);
      if (response.ok) {
        const data = await response.json();
        setRules(data.rules || []);
      }
    } catch (error) {
      console.error("Error fetching rules:", error);
      toast.error("Error al cargar reglas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleSaveRule = async (ruleData) => {
    try {
      const url = editingRule
        ? `${API}/bank/rules/${editingRule.rule_id}`
        : `${API}/bank/rules`;

      const response = await apiFetch(url, {
        method: editingRule ? "PUT" : "POST",
        body: JSON.stringify(ruleData),
      });

      if (response.ok) {
        toast.success(editingRule ? "Regla actualizada" : "Regla creada");
        setDialogOpen(false);
        setEditingRule(null);
        fetchRules();
      } else {
        toast.error("Error al guardar regla");
      }
    } catch (error) {
      toast.error("Error al guardar regla");
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm("¿Eliminar esta regla?")) return;

    try {
      const response = await apiFetch(`${API}/bank/rules/${ruleId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Regla eliminada");
        fetchRules();
      } else {
        toast.error("Error al eliminar regla");
      }
    } catch (error) {
      toast.error("Error al eliminar regla");
    }
  };

  const openEditDialog = (rule) => {
    setEditingRule({
      ...rule,
      conditions: {
        description: rule.conditions?.description?.value || "",
      },
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="bank-rules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Reglas de Clasificación
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Automatiza la clasificación de movimientos bancarios
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => setEditingRule(null)}
              data-testid="new-rule-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Regla
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingRule ? "Editar Regla" : "Nueva Regla"}
              </DialogTitle>
            </DialogHeader>
            <RuleForm
              rule={editingRule}
              onSave={handleSaveRule}
              onCancel={() => {
                setDialogOpen(false);
                setEditingRule(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">
                ¿Cómo funcionan las reglas?
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Las reglas se aplican automáticamente al importar nuevos movimientos.
                Si el concepto contiene el texto especificado, se asigna la categoría y cuenta.
                Las reglas con mayor prioridad se evalúan primero.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rules Table */}
      <Card>
        <CardContent className="p-0">
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">Prior.</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Condición</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Cuenta</TableHead>
                <TableHead className="w-[80px]">Usos</TableHead>
                <TableHead className="w-[80px]">Estado</TableHead>
                <TableHead className="w-[100px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="spinner mx-auto" />
                  </TableCell>
                </TableRow>
              ) : rules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    <Workflow className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay reglas configuradas</p>
                    <p className="text-sm">Crea tu primera regla para automatizar la clasificación</p>
                  </TableCell>
                </TableRow>
              ) : (
                rules.map((rule) => (
                  <TableRow key={rule.rule_id} data-testid={`rule-row-${rule.rule_id}`}>
                    <TableCell className="font-mono text-center">
                      {rule.priority}
                    </TableCell>
                    <TableCell className="font-medium">{rule.name}</TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      Contiene: "{rule.conditions?.description?.value || "-"}"
                    </TableCell>
                    <TableCell>
                      {String(rule.actions?.account_code || "").startsWith("640") ? "Pago de nómina" : (CATEGORIES.find((c) => c.value === rule.actions?.category)
                        ?.label || rule.actions?.category || "-")}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {rule.actions?.category === "nominas" || String(rule.actions?.account_code || "").startsWith("640") ? "Sin asiento" : (rule.actions?.account_code || "-")}
                    </TableCell>
                    <TableCell className="font-mono text-center">
                      {rule.matches_count || 0}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={rule.is_active ? "default" : "secondary"}
                        className={
                          rule.is_active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-500"
                        }
                      >
                        {rule.is_active ? "Activa" : "Inactiva"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(rule)}
                          data-testid={`edit-rule-${rule.rule_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRule(rule.rule_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-rule-${rule.rule_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
